<template>
  <section style="">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <div class="columns">
              <div class="column">
                <form-group
                  name="gift_id"
                  attribute="attributes.gift"
                  :label="$t('message.select') + $t('attributes.gift')"
                  label-position="on-border"
                >
                  <b-input
                    v-if="isUpdate"
                    v-model="gift_name"
                    name="gift_name"
                    expanded
                    disabled
                  >
                  </b-input>
                  <b-autocomplete
                    v-else
                    ref="giftSelect"
                    v-model="search"
                    name="gift_id"
                    field="id"
                    :custom-formatter="t => t.full_name"
                    :data="gifts"
                    :loading="loadingGifts"
                    :placeholder="
                      $t('message.please_select') + $t('attributes.gift')
                    "
                    open-on-focus
                    @typing="onSearchGifts"
                    @select="onSelectGift"
                  >
                    <template #default="props">
                      <p>{{ props.option.full_name }}</p>
                    </template>
                    <template #empty>
                      {{
                        form.gift_id
                          ? loadingGifts
                            ? $t('attributes.searching')
                            : $t('attributes.not_found')
                          : $t('message.please_select') + $t('attributes.gift')
                      }}
                    </template>
                  </b-autocomplete>
                </form-group>

                <form-group
                  name="quantity"
                  attribute="attributes.gift_quantity"
                  :label="$t('attributes.gift_quantity')"
                  label-position="on-border"
                >
                  <b-input
                    v-model.number="form.quantity"
                    type="number"
                    name="quantity"
                    min="1"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_quantity')
                    "
                    expanded
                    @input="updateFinalQuantity"
                  >
                  </b-input>
                </form-group>

                <div class="columns">
                  <div class="column">
                    <form-group
                      name="origin_quantity"
                      attribute="attributes.gift_origin_quantity"
                      :label="$t('attributes.gift_origin_quantity')"
                      label-position="on-border"
                    >
                      <b-input
                        v-model.number="form.origin_quantity"
                        type="number"
                        name="origin_quantity"
                        disabled
                        expanded
                      >
                      </b-input>
                    </form-group>

                    <form-group
                      name="final_quantity"
                      attribute="attributes.gift_final_quantity"
                      :label="$t('attributes.gift_final_quantity')"
                      label-position="on-border"
                    >
                      <b-input
                        v-model.number="form.final_quantity"
                        type="number"
                        name="final_quantity"
                        disabled
                        expanded
                      >
                      </b-input>
                    </form-group>
                  </div>

                  <div class="column">
                    <form-group
                      name="origin_stock"
                      attribute="attributes.gift_origin_stock"
                      :label="$t('attributes.gift_origin_stock')"
                      label-position="on-border"
                    >
                      <b-input
                        v-model.number="form.origin_stock"
                        type="number"
                        name="origin_stock"
                        disabled
                        expanded
                      >
                      </b-input>
                    </form-group>

                    <form-group
                      name="final_stock"
                      attribute="attributes.gift_final_stock"
                      :label="$t('attributes.gift_final_stock')"
                      label-position="on-border"
                    >
                      <b-input
                        v-model.number="form.final_stock"
                        type="number"
                        name="final_stock"
                        disabled
                        expanded
                      >
                      </b-input>
                    </form-group>
                  </div>
                </div>

                <form-group
                  name="remark"
                  :label="$t('attributes.remark')"
                  label-position="on-border"
                >
                  <b-input
                    v-model="form.remark"
                    type="textarea"
                    name="amount"
                    maxlength="200"
                    expanded
                  >
                  </b-input>
                </form-group>
              </div>

              <div class="column">
                <form-group
                  name="date"
                  attribute="attributes.gift_transfer_date"
                  :label="$t('attributes.gift_transfer_date')"
                  label-position="on-border"
                >
                  <b-datepicker
                    v-model="transferDate"
                    :placeholder="
                      $t('message.please_input') +
                        $t('attributes.gift_transfer_date')
                    "
                    icon="calendar-today"
                    inline
                    trap-focus
                    @input="onSelectedDate"
                  >
                    <div class="buttons">
                      <b-button
                        :label="$t('attributes.today')"
                        type="is-success"
                        @click="onSelectedToday"
                      />
                      <b-button
                        :label="$t('attributes.yesterday')"
                        type="is-info"
                        @click="onSelectedYesterday"
                      />
                      <b-button
                        :label="$t('attributes.clear')"
                        type="is-danger"
                        @click="onClearDate"
                      />
                    </div>
                  </b-datepicker>
                </form-group>
              </div>
            </div>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isNew ? $t('attributes.add') : $t('attributes.update') }}
          </b-button>
        </footer>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, numeric, between } from 'vuelidate/lib/validators'
import { GIFTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'

export default {
  name: 'GiftTransferImportForm',

  props: {
    giftTransfer: {
      type: Object,
      required: true
    },
    sending: {
      type: Boolean,
      required: true
    }
  },
  data() {
    const isNew = !this.giftTransfer.id
    const transferDate =
      this.giftTransfer.date && this.$dayjs(this.giftTransfer.date).toDate()

    return {
      isNew: isNew,
      isUpdate: !isNew,
      title: this.$t('attributes.gift_transfer_import'),

      search: null,
      loadingGifts: false,
      gifts: [],
      selectedGift: null,
      gift_name: this.giftTransfer.gift_name,
      transferDate: transferDate,

      form: {
        gift_id: this.giftTransfer.gift_id,
        date: this.giftTransfer.date,
        quantity: this.giftTransfer.quantity,
        origin_quantity: this.giftTransfer.origin_quantity,
        final_quantity: this.giftTransfer.final_quantity,
        origin_stock: this.giftTransfer.origin_stock,
        final_stock: this.giftTransfer.final_stock,
        remark: this.giftTransfer.remark
      }
    }
  },
  computed: {
    isLoading() {
      return this.loadingGifts || this.sending
    }
  },
  validations() {
    return {
      form: {
        gift_id: { required },
        date: { required },
        quantity: {
          required,
          numeric,
          between: between(
            1,
            this.selectedGift ? this.selectedGift.stock : 100000
          )
        },
        origin_quantity: { required },
        final_quantity: { required },
        origin_stock: { required },
        final_stock: { required },
        remark: {}
      }
    }
  },
  mounted() {
    if (this.isNew && !this.giftTransfer.gift_id) {
      this.$refs.giftSelect.focus()
    }

    this.loadGiftsAsync(null)
  },
  methods: {
    async loadGiftsAsync(search) {
      try {
        const api = `${GIFTS_API}`
        const params = {
          search: search
        }

        this.loadingGifts = true
        const res = await this.axios.get(api, { params })
        this.gifts = res.data.data

        const gift = this.gifts.find(d => d.id === this.form.gift_id) || null

        if (this.isNew && gift) {
          this.$refs.giftSelect.setSelected(gift)
        }
        this.onSelectGift(gift)
      } catch (e) {
        this.gifts = []
        this.onSelectGift(null)

        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingGifts = false
      }
    },
    onSearchGifts: debounce(function(search) {
      this.loadGiftsAsync(search)
    }, 500),
    onSelectGift(gift) {
      this.selectedGift = gift
      this.form.gift_id = gift && gift.id
      this.form.origin_stock = gift ? gift.stock : 0
      this.form.origin_quantity = gift ? gift.quantity : 0
      this.updateFinalQuantity()
    },
    onSelectedToday() {
      this.transferDate = this.$dayjs().toDate()
      this.form.date = this.formatDate(this.transferDate)
    },
    onSelectedYesterday() {
      this.transferDate = this.$dayjs()
        .subtract(1, 'day')
        .toDate()
      this.form.date = this.formatDate(this.transferDate)
    },
    onClearDate() {
      this.transferDate = null
      this.form.date = null
    },
    onSelectedDate(date) {
      this.form.date = this.formatDate(date)
    },
    updateFinalQuantity() {
      this.form.final_stock = this.form.origin_stock - this.form.quantity
      this.form.final_quantity = this.form.origin_quantity + this.form.quantity
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
